import React, { useState, useEffect, useRef, useCallback, useTransition } from 'react';
import { RefreshCw, AlertCircle, ChevronLeft, ChevronRight, Eye, Loader2, Square } from 'lucide-react';
import authService from '../services/AuthService';
import { API_BASE, getSupplierLogo } from '../consts';
import AdvancedFilters from './AdvancedFilters';
import DealCard from './DealCard';
import MinimizedDealCard from './MinimizedDealCard';
import { calculateReferralFee, calculateWFSFee, detectPackCount } from '../utils';

const INTIIAL_FILTERS = {
    marketplace: [],
    autoPackDetection: true,
    remove_multipacks: false,
    min_roi: -1,
    max_roi: -1,
    min_profit: -1,
    max_profit: -1,
    profitable_only: false,
    search_text: '',
    min_cost: -1,
    max_cost: -1,
    min_sell_price: -1,
    max_sell_price: -1,
    coupon_amount: 0, // Percentage discount
    days_since_found: -1, // -1 means no filter
    favorites_only: false,
    amazon: {
        exclude_amazon_selling: false,
        only_amazon_selling: false,
        exclude_walmart_3p: false,
        no_variations: false,
        no_private_label: false,
        min_bsr: -1,
        max_bsr: -1,
        min_bsr_percent: -1,
        max_bsr_percent: -1,
        min_offers: -1,
        max_offers: -1,
        min_bsr_drops_30: -1,
        min_bsr_drops_90: -1,
        min_est_sales: -1,
        max_weight: -1,
        gated_status: 'all',
        fulfillment: 'fba'
    },
    walmart: {
        exclude_walmart_selling: false,
        only_walmart_selling: false,
        exclude_walmart_3p: false,
        fulfillment: 'wfs',
        min_offers: -1,
        max_offers: -1,
        in_stock_only: false,
        min_reviews: -1,
        min_rating: -1,
        selected_badges: []
    },
    ebay: {
        exclude_walmart_3p: false,
        min_offers: -1,
        max_offers: -1,
        min_est_sales: -1
    },
    suppliers: {
        selected_suppliers: [],  // Whitelist - empty means show all
        banned_suppliers: []     // Blacklist - stored in localStorage
    },
    shared: {
        selected_categories: [],
        selected_brands: []
    }
};

const getEffectivePack = (deal, autoPackDetection = true) => {
    try {
        const pack = deal.marketplace_data?.pack;
        if (pack && pack > 1) {
            const sp = deal.source_price || 0;
            const azPrice = deal.marketplace_data?.az_price || 0;
            const fbaFee = deal.marketplace_data?.fba_fee || 0;
            const refFee = azPrice * (deal.marketplace_data?.ref_fee_perc || 0.15);
            const roi = sp > 0 ? ((azPrice - sp - fbaFee - refFee) / sp) * 100 : 0;
            if (roi > 100) return pack;
        }
        if (!autoPackDetection) return 1;
        const titleToCheck = deal.marketplace === 'amazon'
            ? deal.marketplace_data?.title || deal.title || ''
            : deal.marketplace === 'walmart'
                ? deal.marketplace_data?.name || deal.marketplace_data?.title || deal.title || ''
                : deal.title || '';
        return detectPackCount(titleToCheck);
    } catch {
        return 1;
    }
};

// Export to CSV with ALL possible data
const exportToCSV = (filteredDeals, filters) => {
    const deals = filteredDeals.map(g => g.primaryDeal).filter(d => !d.is_skeleton);

    if (deals.length === 0) {
        alert('No deals to export!');
        return;
    }

    // Comprehensive CSV Headers
    const headers = [
        'Title',
        'Supplier',
        'Source Price',
        'Coupon/Discount',
        'Effective Cost',
        'Marketplace',
        'Sell Price',
        'Profit',
        'ROI (%)',
        'Total Fees',
        'FBA/WFS Fee',
        'Referral Fee',
        'Placement Fees',
        'Inbound Shipping',
        'Sales Tax',
        'Packaging Cost',
        'Other Costs',
        'ASIN/Item ID',
        'UPC',
        'Brand',
        'Category',
        'BSR',
        'BSR %',
        'BSR Avg 30d',
        'BSR Avg 90d',
        'Offers',
        'Variations',
        'BSR Drops 30d',
        'BSR Drops 90d',
        'Est. Sales',
        'Keepa Monthly Sold',
        'Gated Status',
        'Amazon Sells',
        'Walmart Sells',
        'In Stock',
        'Reviews',
        'Rating',
        'Weight',
        'Product URL',
        'Marketplace URL',
        window.location.pathname.includes('storefront-scan') ? 'First Seen' : 'Last Updated',
    ];

    // Build CSV rows with ALL data
    const rows = deals.map(deal => {
        const data = deal.marketplace_data || {};
        const couponPercent = filters.coupon_amount || 0;
        const couponAmount = (deal.source_price || 0) * (couponPercent / 100);
        const sourcePrice = ((deal.source_price || 0) - couponAmount) * getEffectivePack(deal, filters.autoPackDetection);

        // Get fee settings from localStorage
        const getFeeSettings = (marketplace) => {
            const storageKey = `${marketplace}_fee_settings`;
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved) : {};
        };

        const feeSettings = getFeeSettings(deal.marketplace);
        const itemWeight = data.weight || deal.weight || 1;

        // Calculate metrics with ALL fees
        let sellPrice = 0;
        let profit = 0;
        let roi = 0;
        let totalFees = 0;
        let fbaFee = 0;
        let refFee = 0;
        let placementFees = 0;
        let inboundShipping = 0;
        let salesTax = 0;
        let packagingCost = 0;
        let otherCosts = 0;

        if (deal.marketplace === 'amazon') {
            sellPrice = data.az_price || 0;
            fbaFee = filters.amazon?.fulfillment === 'fba' ? (data.fba_fee || 0) : 0;
            const refFeePerc = data.ref_fee_perc || 0.15;
            refFee = sellPrice * refFeePerc;
            placementFees = filters.amazon?.fulfillment === 'fba' ? (feeSettings.placementFees || 0) : 0;
            inboundShipping = filters.amazon?.fulfillment === 'fba' ? (feeSettings.inboundShipping || 0) * itemWeight : 0;
            salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
            otherCosts = feeSettings.otherCosts || 0;
            totalFees = fbaFee + refFee + placementFees + inboundShipping + salesTax + otherCosts;
            profit = sellPrice - sourcePrice - totalFees;
            roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;
        } else if (deal.marketplace === 'walmart') {
            sellPrice = data.price_value || 0;
            const wfsFee = filters.walmart?.fulfillment === 'wfs' ? (
                (data.dimensions && itemWeight)
                    ? calculateWFSFee(data.dimensions, itemWeight, {
                        isApparel: data.category?.toLowerCase().includes("clothing") || data.category?.toLowerCase().includes("apparel"),
                        retailPrice: sellPrice
                    })
                    : 3.45
            ) : 0;
            fbaFee = wfsFee;
            refFee = calculateReferralFee(sellPrice, data.category, data.sub_category);
            inboundShipping = filters.walmart?.fulfillment === 'wfs' ? (feeSettings.inboundShipping || 0) * itemWeight : 0;
            salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
            otherCosts = feeSettings.otherCosts || 0;
            totalFees = wfsFee + refFee + inboundShipping + salesTax + otherCosts;
            profit = sellPrice - sourcePrice - totalFees;
            roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;
        } else if (deal.marketplace === 'ebay') {
            sellPrice = data.lowest_price || 0;
            const totalSale = sellPrice;
            let finalValueFee = totalSale <= 7500 ? totalSale * 0.136 : (7500 * 0.136) + ((totalSale - 7500) * 0.0235);
            const storeDiscount = { none: 0, basic: 0.066, premium: 0.088, anchor: 0.102, enterprise: 0.110 }[feeSettings.storeSubscription || 'none'] || 0;
            const topRatedDiscount = feeSettings.topRated ? 0.10 : 0;
            finalValueFee = finalValueFee * (1 - storeDiscount - topRatedDiscount);
            const perOrderFee = totalSale <= 10 ? 0.30 : 0.40;
            const paymentProcessingFee = (totalSale * 0.0235) + 0.30;
            refFee = finalValueFee + perOrderFee + paymentProcessingFee;
            salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
            packagingCost = feeSettings.packagingCost || 0;
            otherCosts = feeSettings.otherCosts || 0;
            totalFees = refFee + salesTax + packagingCost + otherCosts;
            profit = sellPrice - sourcePrice - totalFees;
            roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;
        }

        const cleanTitle = (deal.title || '').replace(/\(Seller:\s*[^)]+\)\s*/gi, '').trim();

        const marketplaceUrl =
            deal.marketplace === 'amazon' && data.asin
                ? `https://www.amazon${authService.getAmazonMarketplace()}/dp/${data.asin}`
                : deal.marketplace === 'walmart' && data.item_id
                    ? `https://www.walmart.com/ip/${data.item_id}`
                    : deal.marketplace === 'ebay' && data.ebay_search_link
                        ? data.ebay_search_link
                        : '';

        return [
            `"${cleanTitle.replace(/"/g, '""')}"`,
            `"${(deal.supplier_name || '').replace(/"/g, '""')}"`,
            (deal.source_price || 0).toFixed(2),
            `${couponPercent}%`,
            sourcePrice.toFixed(2),
            deal.marketplace,
            sellPrice.toFixed(2),
            profit.toFixed(2),
            roi.toFixed(2),
            totalFees.toFixed(2),
            fbaFee.toFixed(2),
            refFee.toFixed(2),
            placementFees.toFixed(2),
            inboundShipping.toFixed(2),
            salesTax.toFixed(2),
            packagingCost.toFixed(2),
            otherCosts.toFixed(2),
            data.asin || data.item_id || '',
            deal.upc || '',
            `"${(data.brand || '').replace(/"/g, '""')}"`,
            `"${(data.category || '').replace(/"/g, '""')}"`,
            data.sales_rank || '',
            data.sales_rank_perc?.toFixed(2) || '',
            data.bsr_avg30 || '',
            data.bsr_avg90 || '',
            data.total_offers_count || data.offers || data.total_offers || '',
            Array.isArray(data.variations) ? data.variations.length : (data.variations || ''),
            data.sales_rank_drops_30_days || '',
            data.sales_rank_drops_90_days || '',
            data.estimate_sales_30_days || data.estimated_monthly_sales || '',
            data.keepa_monthly_sold || '',
            deal.gated_status || '',
            data.az_sells_it ? 'Yes' : 'No',
            data.is_walmart ? 'Yes' : 'No',
            data.in_stock ? 'Yes' : 'No',
            data.number_of_reviews || '',
            data.average_rating || '',
            itemWeight,
            `"${(deal.product_url || '').replace(/"/g, '""')}"`,
            `"${marketplaceUrl.replace(/"/g, '""')}"`,
            (() => {
                const isStorefront = window.location.pathname.includes('storefront-scan');
                const dateStr = isStorefront ? deal.created_at : (deal.updated_at || deal.created_at);
                if (!dateStr) return '';
                const d = new Date(dateStr.replace(' ', 'T') + (/(Z|[+-]\d{2}:\d{2})$/.test(dateStr) ? '' : 'Z'));
                return `"${isNaN(d) ? dateStr : d.toLocaleString()}"`;
            })(),
        ].join(',');
    });

    // Combine headers and rows
    const csv = [headers.join(','), ...rows].join('\n');

    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `deals_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
};

// Small editable "Page X of Y" control — click the number to type a page directly
const PageJumpInput = ({ currentPage, totalPages, onJump }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(String(currentPage));

    useEffect(() => {
        if (!editing) setValue(String(currentPage));
    }, [currentPage, editing]);

    const commit = () => {
        const parsed = parseInt(value, 10);
        if (!isNaN(parsed)) {
            const clamped = Math.min(Math.max(parsed, 1), totalPages);
            onJump(clamped);
        }
        setEditing(false);
    };

    if (editing) {
        return (
            <span className="text-gray-700 font-medium flex items-center gap-1">
                Page{' '}
                <input
                    type="number"
                    min={1}
                    max={totalPages}
                    autoFocus
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') { setValue(String(currentPage)); setEditing(false); }
                    }}
                    className="w-14 px-1 py-0.5 border border-teal-400 rounded text-center focus:outline-none focus:ring-2 focus:ring-teal-300"
                />{' '}
                of {totalPages}
            </span>
        );
    }

    return (
        <span
            onClick={() => setEditing(true)}
            className="text-gray-700 font-medium cursor-pointer hover:underline decoration-dotted"
            title="Click to jump to a page"
        >
            Page {currentPage} of {totalPages}
        </span>
    );
};

// Placeholder row for a storefront product whose live data hasn't been fetched yet (on-demand mode only)
const SkeletonDealRow = ({ deal }) => (
    <div className="border border-gray-200 rounded-lg bg-white p-3 flex items-center gap-4">
        {deal.is_new && (
            <span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">NEW</span>
        )}
        <div className="w-16 h-16 rounded bg-gray-200 animate-pulse" />
        <div className="w-16 h-16 rounded bg-gray-200 animate-pulse" />
        <div className="flex-1 space-y-2">
            <div className="h-3 bg-gray-200 rounded w-2/3 animate-pulse" />
            <div className="h-3 bg-gray-100 rounded w-1/3 animate-pulse" />
            <div className="text-xs text-gray-400 flex items-center gap-2">
                <span className="font-mono">{deal.product_id}</span>
                {deal.marketplace === 'amazon' && (
                    <>
                        <a href={`https://www.amazon${authService.getAmazonMarketplace()}/dp/${deal.product_id}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 hover:underline font-semibold">Amazon ↗</a>
                        <a href={`https://keepa.com/#!product/${{ '.com': '1', '.co.uk': '2', '.ca': '6', '.com.mx': '8' }[authService.getAmazonMarketplace()] || '1'}-${deal.product_id}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 hover:underline font-semibold">Keepa ↗</a>
                    </>
                )}
                {deal.marketplace === 'walmart' && (
                    <a href={`https://www.walmart.com/ip/${deal.product_id}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 hover:underline font-semibold">Walmart ↗</a>
                )}
                {deal.marketplace === 'ebay' && (
                    <a href={`https://www.ebay.com/itm/${deal.product_id}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 hover:underline font-semibold">eBay ↗</a>
                )}
            </div>
        </div>
        {deal.fetch_failed ? (
            <span className="text-xs text-red-500 font-semibold">Couldn't load data</span>
        ) : (
            <span className="text-xs text-gray-400 flex items-center gap-1">
                <Loader2 className="animate-spin" size={12} /> Loading live data…
            </span>
        )}
    </div>
);

// Main DealsViewer Component
const DealsViewer = ({
    deals,
    loading = false,
    title = "Deals",
    showFilters = true,
    pageName = "live-scan",
    user = null,
    defaultAutoPackDetection = true
}) => {
    const initialFilters = { ...INTIIAL_FILTERS, autoPackDetection: defaultAutoPackDetection };
    const [filters, setFilters] = useState(initialFilters);
    const [filteredDeals, setFilteredDeals] = useState([]);
    const [sortBy, setSortBy] = useState('default');
    const [isPending, startTransition] = useTransition();
    const [refreshing, setRefreshing] = useState(false);
    const [settingsUpdateTrigger, setSettingsUpdateTrigger] = useState(0);
    const [hiddenDeals, setHiddenDeals] = useState(() => {
        const saved = localStorage.getItem('hidden_deals');
        return saved ? JSON.parse(saved) : [];
    });

    const [isCompactView, setIsCompactView] = useState(() => {
        const stored = localStorage.getItem('compact_deal_view');
        return stored === null ? true : stored === 'true';
    });

    const [afRender, setAFRender] = useState(0);

    // Reveal All state
    const [revealingAll, setRevealingAll] = useState(false);
    const [revealProgress, setRevealProgress] = useState({ current: 0, total: 0 });
    const revealAbortRef = useRef(false);
    const [showRevealWarning, setShowRevealWarning] = useState(false);
    const [showSupplierPicker, setShowSupplierPicker] = useState(false);
    const [supplierPickerData, setSupplierPickerData] = useState({ suppliers: [], max_suppliers: 10, is_unlimited: false });
    const [selectedSuppliers, setSelectedSuppliers] = useState([]);
    const [showSearchingPopup, setShowSearchingPopup] = useState(false);

    // Expose supplier picker globally so individual deal cards can trigger it
    useEffect(() => {
        window.__msp_openSupplierPicker = async () => {
            const res = await authService.authenticatedFetch(API_BASE + '/api/reverse-scan/suppliers');
            const data = await res.json();
            setSupplierPickerData(data);
            setShowRevealWarning(true);
        };
        return () => { delete window.__msp_openSupplierPicker; };
    }, []);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const dealsPerPage = isCompactView ? 25 : 10;

    // Calculate pagination
    const indexOfLastDeal = currentPage * dealsPerPage;
    const indexOfFirstDeal = indexOfLastDeal - dealsPerPage;
    const currentDeals = filteredDeals.slice(indexOfFirstDeal, indexOfLastDeal);
    const totalPages = Math.ceil(filteredDeals.length / dealsPerPage);
    const amazonLinked = user?.amazon_linked_account && Object.keys(user.amazon_linked_account).length > 0;

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, isCompactView, title]);

    useEffect(() => {
        setFilters(initialFilters);
    }, [title]);

    const unrevealedTotal = filteredDeals
        .map(g => g.primaryDeal)
        .filter(d => d.reverse_scan_status === 'not_scanned' && d.upc && String(d.upc).trim().replace(/\s/g, '').match(/^\d+$/));

    const handleRevealAll = useCallback(async (suppliersToUse) => {
        const unrevealed = filteredDeals
            .map(g => g.primaryDeal)
            .filter(d => d.reverse_scan_status === 'not_scanned' && d.upc && String(d.upc).trim().replace(/\s/g, '').match(/^\d+$/));

        if (unrevealed.length === 0) return;

        setRevealingAll(true);
        revealAbortRef.current = false;
        setRevealProgress({ current: 0, total: unrevealed.length });

        const isStorefront = window.location.pathname.includes('storefront');
        const endpoint = isStorefront ? '/api/storefront/reverse-scan/trigger' : '/api/reverse-scan/deals/trigger';

        try {
            const allIds = unrevealed.map(d => d.id);
            const res = await authService.authenticatedFetch(API_BASE + endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ deal_ids: allIds, selected_suppliers: suppliersToUse || null })
            });
            const data = await res.json();

            if (res.ok) {
                // Check if we were trimmed due to the 2000 product limit
                if (data.limit_reached) {
                    alert(`${data.queued_count} out of ${unrevealed.length} products were queued. Wait for some to finish before revealing more, or email us to increase your limit.`);
                }
                unrevealed.forEach(d => d.reverse_scan_status = 'pending');
                setSettingsUpdateTrigger(p => p + 1);
                setShowSearchingPopup(true);
            } else {
                alert(data.message || data.error || 'Failed to queue reverse scan.');
            }
        } catch (e) { console.error(e); }
        setRevealingAll(false);
    }, [filteredDeals]);

    const stopRevealAll = () => { revealAbortRef.current = true; };

    // Group deals by marketplace product ID (ASIN, Walmart ID, or eBay ID)
    const groupDealsByProduct = (deals) => {
        const grouped = {};

        deals.forEach(deal => {
            // Extract marketplace product ID from marketplace_data
            const marketplaceData = deal.marketplace_data || {};
            let marketplaceProductId = null;

            if (deal.marketplace === 'amazon') {
                marketplaceProductId = marketplaceData.asin;
            } else if (deal.marketplace === 'walmart') {
                marketplaceProductId = marketplaceData.item_id;
            } else if (deal.marketplace === 'ebay') {
                marketplaceProductId = deal.upc; // or could use marketplace_data.ebay_search_link
            }

            // Create a unique key combining marketplace and product ID
            const groupKey = marketplaceProductId
                ? `${deal.marketplace}_${marketplaceProductId}`
                : `standalone_${deal.id}`;

            if (!grouped[groupKey]) {
                // First deal with this marketplace product
                grouped[groupKey] = {
                    primaryDeal: deal,
                    alternativeSources: []
                };
            } else {
                // Compare prices and keep the lowest as primary
                const currentPrimary = grouped[groupKey].primaryDeal;
                const currentPrimaryPrice = currentPrimary.source_price || 0;
                const newDealPrice = deal.source_price || 0;

                if (newDealPrice < currentPrimaryPrice) {
                    // New deal is cheaper - make it primary, move old primary to alternatives
                    grouped[groupKey].alternativeSources.push(currentPrimary);
                    grouped[groupKey].primaryDeal = deal;
                } else {
                    // Current primary is still cheaper - add new deal to alternatives
                    grouped[groupKey].alternativeSources.push(deal);
                }
            }
        });

        return Object.values(grouped);
    };

    const getAllCategories = () => {
        const categorySet = new Set();
        deals.forEach(deal => {
            const data = deal.marketplace_data || {};
            let category = null;
            if (deal.marketplace === 'amazon') {
                category = data.category;
            } else if (deal.marketplace === 'walmart') {
                category = data.category;
            } else if (deal.marketplace === 'ebay') {
                category = data.category;
            }
            if (category && category.trim()) {
                categorySet.add(category.trim());
            }
        });
        return Array.from(categorySet).sort();
    };

    const getAllBrands = () => {
        const brandSet = new Set();
        deals.forEach(deal => {
            const brand = deal.marketplace_data?.brand;
            if (brand && String(brand).trim()) brandSet.add(String(brand).trim());
        });
        return Array.from(brandSet).sort((a, b) => a.localeCompare(b));
    };

    const getAllWalmartBadges = () => {
        const badgeSet = new Set();
        deals.forEach(deal => {
            if (deal.marketplace === 'walmart' && deal.marketplace_data?.badges) {
                deal.marketplace_data.badges.forEach(badge => {
                    if (badge.text) {
                        badgeSet.add(badge.text);
                    }
                });
            }
        });
        return Array.from(badgeSet).sort();
    };

    const getAllSuppliers = () => {
        const supplierMap = new Map();
        deals.forEach(deal => {
            const supplierName = deal.supplier_name;
            if (supplierName && !supplierMap.has(supplierName)) {
                supplierMap.set(supplierName, {
                    name: supplierName,
                    logo: getSupplierLogo(supplierName)
                });
            }
        });
        return Array.from(supplierMap.values()).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    };

    // Helper function to get fee settings
    const getFeeSettings = (marketplace) => {
        const storageKey = `${marketplace}_fee_settings`;
        const saved = localStorage.getItem(storageKey);
        return saved ? JSON.parse(saved) : {};
    };

    // Filter deals whenever filters or deals change
    React.useEffect(() => {
        // On-demand storefront: skeleton rows (not fetched yet) skip filters/sorting and are appended at the end
        const skeletonGroups = deals.filter(d => d.is_skeleton)
            .map(d => ({ primaryDeal: d, alternativeSources: [] }))
            .sort((a, b) => (a.primaryDeal.fetch_failed ? 1 : 0) - (b.primaryDeal.fetch_failed ? 1 : 0)); // failed ones go last
        let result = deals.filter(d => !d.is_skeleton);

        // Filter out hidden deals
        result = result.filter(deal => !hiddenDeals.includes(deal.id));

        // Remove multipacks (uses same detection as Multi Pack Detection)
        if (filters.remove_multipacks) {
            result = result.filter(deal => getEffectivePack(deal, true) <= 1);
        }

        // Favorites filter
        if (filters.favorites_only) {
            result = result.filter(deal => !!deal.favorite_id);
        }

        if (filters.days_since_found > 0) {
            const cutoff = new Date();
            cutoff.setDate(cutoff.getDate() - filters.days_since_found);
            result = result.filter(deal => {
                const dateStr = deal.created_at;
                try {
                    const d = new Date(dateStr.replace(' ', 'T') + (/(Z|[+-]\d{2}:\d{2})$/.test(dateStr) ? '' : 'Z'));
                    return d >= cutoff;
                } catch { return true; }
            });
        }

        // Marketplace filter
        if (filters.marketplace.length > 0) {
            result = result.filter(deal => filters.marketplace.includes(deal.marketplace));
        }

        // Shared filters
        if (filters.search_text) {
            const searchLower = filters.search_text.toLowerCase();
            result = result.filter(deal => {
                const title = (deal.title || '').toLowerCase();
                const brand = (deal.marketplace_data?.brand || '').toLowerCase();
                const asin = (deal.marketplace_data?.asin || '').toLowerCase();
                const itemId = (deal.marketplace_data?.item_id || '').toLowerCase();
                const upc = (deal.upc || '').toLowerCase();

                return title.includes(searchLower) ||
                    brand.includes(searchLower) ||
                    asin.includes(searchLower) ||
                    itemId.includes(searchLower) ||
                    upc.includes(searchLower);
            });
        }

        // Calculate coupon amount as percentage
        const couponPercent = filters.coupon_amount || 0;

        if (filters.min_cost > 0) {
            result = result.filter(deal => {
                const couponAmount = (deal.source_price || 0) * (couponPercent / 100);
                const effectiveCost = (deal.source_price || 0) - couponAmount;
                return effectiveCost >= filters.min_cost;
            });
        }

        if (filters.max_cost > 0) {
            result = result.filter(deal => {
                const couponAmount = (deal.source_price || 0) * (couponPercent / 100);
                const effectiveCost = (deal.source_price || 0) - couponAmount;
                return effectiveCost <= filters.max_cost;
            });
        }

        // Calculate metrics and apply all filters
        result = result.filter(deal => {
            const couponAmount = (deal.source_price || 0) * (couponPercent / 100);
            const sourcePrice = ((deal.source_price || 0) - couponAmount) * getEffectivePack(deal, filters.autoPackDetection);
            const data = deal.marketplace_data || {};
            const feeSettings = getFeeSettings(deal.marketplace);
            const itemWeight = data.weight || deal.weight || 1;

            let sellPrice = 0;
            let profit = 0;
            let roi = 0;

            // Supplier filtering - BANNED suppliers (blacklist)
            const bannedSuppliers = filters.suppliers.banned_suppliers || [];
            if (bannedSuppliers.length > 0) {
                const supplierName = (deal.supplier_name || '').toLowerCase();
                if (bannedSuppliers.some(banned => banned.toLowerCase() === supplierName)) {
                    return false;
                }
            }

            // Supplier filtering - SELECTED suppliers (whitelist)
            if (filters.suppliers.selected_suppliers.length > 0) {
                const supplierName = (deal.supplier_name || '').toLowerCase();
                const isSelected = filters.suppliers.selected_suppliers.some(
                    selected => selected.toLowerCase() === supplierName.toLowerCase()
                );
                if (!isSelected) return false;
            }

            // Brand whitelist (show only selected brands)
            const selectedBrands = filters.shared?.selected_brands || [];
            if (selectedBrands.length > 0) {
                const dealBrand = String(data.brand || '').trim();
                if (!selectedBrands.includes(dealBrand)) return false;
            }

            // Calculate based on marketplace with ALL fees
            if (deal.marketplace === 'amazon') {
                sellPrice = data.az_price || 0;
                const fbaFee = filters.amazon.fulfillment === 'fba' ? (data.fba_fee || 0) : 0;
                const refFeePerc = data.ref_fee_perc || 0.15;
                const refFee = sellPrice * refFeePerc;
                const placementFees = filters.amazon.fulfillment === 'fba' ? (feeSettings.placementFees || 0) : 0;
                const inboundShipping = filters.amazon.fulfillment === 'fba' ? (feeSettings.inboundShipping || 0) * itemWeight : 0;
                const salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
                const otherCosts = feeSettings.otherCosts || 0;

                const totalFees = fbaFee + refFee + placementFees + inboundShipping + salesTax + otherCosts;
                profit = sellPrice - sourcePrice - totalFees;
                roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

                // Amazon-specific filters
                if (filters.amazon.exclude_amazon_selling && data.az_sells_it) return false;
                if (filters.amazon.only_amazon_selling && !data.az_sells_it) return false;
                if (filters.amazon.no_variations && (data.variations || 0) > 1) return false;
                if (filters.amazon.no_private_label && (data.total_offers_count || 0) < 4) return false;

                // Walmart 3P filter for Amazon marketplace
                if (filters.amazon.exclude_walmart_3p) {
                    const supplierLower = (deal.supplier_name || '').toLowerCase();
                    const titleLower = (deal.title || '').toLowerCase();
                    const hasWalmartSeller = titleLower.includes('(seller: walmart.com)') || titleLower.includes('(seller: walmart)');
                    if (supplierLower === 'walmart' && !hasWalmartSeller) return false;
                }

                // Brand exclusion (shared)
                const excludedBrands = JSON.parse(localStorage.getItem('excluded_brands') || '[]');
                const brand = (data.brand || '').toLowerCase();
                if (excludedBrands.some(b => brand === b.toLowerCase())) return false;

                // Category filter (shared) - ADD THIS
                if (filters.shared.selected_categories.length > 0) {
                    const dealCategory = data.category || '';
                    if (!filters.shared.selected_categories.includes(dealCategory)) {
                        return false;
                    }
                }

                if (filters.amazon.min_bsr > 0 && (data.sales_rank || 0) < filters.amazon.min_bsr) return false;
                if (filters.amazon.max_bsr > 0 && ((data.sales_rank || 0) > filters.amazon.max_bsr || (data.sales_rank || 0) <= 0)) return false;

                if (filters.amazon.min_bsr_percent > 0 && (data.sales_rank_perc || 0) < filters.amazon.min_bsr_percent) return false;
                if (filters.amazon.max_bsr_percent > 0 && ((data.sales_rank_perc || 0) > filters.amazon.max_bsr_percent || (data.sales_rank_perc || 0) <= 0)) return false;

                if (filters.amazon.min_offers >= 0 && (data.total_offers_count || 0) < filters.amazon.min_offers) return false;
                if (filters.amazon.max_offers >= 0 && (data.total_offers_count || 0) > filters.amazon.max_offers) return false;

                if (filters.amazon.min_bsr_drops_30 > 0 && (data.sales_rank_drops_30_days || 0) < filters.amazon.min_bsr_drops_30) return false;
                if (filters.amazon.min_bsr_drops_90 > 0 && (data.sales_rank_drops_90_days || 0) < filters.amazon.min_bsr_drops_90) return false;

                const estSales = data.keepa_monthly_sold > 0
                    ? data.keepa_monthly_sold
                    : (data.estimate_sales_30_days || 0);
                if (filters.amazon.min_est_sales > 0 && estSales < filters.amazon.min_est_sales) return false;
                if (filters.amazon.max_weight > 0 && (data.weight || 0) > filters.amazon.max_weight) return false;

                if (filters.amazon.gated_status !== 'all') {
                    if (filters.amazon.gated_status === 'not_gated') {
                        // Exclude gated and approval - show ungated, checking, error, needs_login
                        if (deal.gated_status === 'gated') {
                            return false;
                        }
                    } else {
                        // Exact match for other filters (ungated, checking)
                        if (deal.gated_status !== filters.amazon.gated_status) {
                            return false;
                        }
                    }
                }

            } else if (deal.marketplace === 'walmart') {
                sellPrice = data.price_value || 0;
                const wfsFee = filters.walmart.fulfillment === 'wfs' ? (
                    (data.dimensions && itemWeight)
                        ? calculateWFSFee(data.dimensions, itemWeight, {
                            isApparel: data.category?.toLowerCase().includes("clothing") || data.category?.toLowerCase().includes("apparel"),
                            retailPrice: sellPrice
                        })
                        : 3.45
                ) : 0;
                const refFee = calculateReferralFee(sellPrice, data.category, data.sub_category);
                const inboundShipping = filters.walmart.fulfillment === 'wfs' ? (feeSettings.inboundShipping || 0) * itemWeight : 0;
                const salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
                const otherCosts = feeSettings.otherCosts || 0;
                const totalFees = wfsFee + refFee + inboundShipping + salesTax + otherCosts;
                profit = sellPrice - sourcePrice - totalFees;
                roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

                // Walmart-specific filters
                if (filters.walmart.exclude_walmart_selling && data.is_walmart) return false;
                if (filters.walmart.only_walmart_selling && !data.is_walmart) return false;
                if (filters.walmart.in_stock_only && !data.in_stock) return false;
                if (filters.walmart.seller_in_stock_only && window.location.pathname.includes("storefront-scan") && deal.storefront_scanned_instock !== null && !deal.storefront_scanned_instock) return false;

                // Walmart 3P filter for Walmart marketplace
                if (filters.walmart.exclude_walmart_3p) {
                    const supplierLower = (deal.supplier_name || '').toLowerCase();
                    const titleLower = (deal.title || '').toLowerCase();
                    const hasWalmartSeller = titleLower.includes('(seller: walmart.com)') || titleLower.includes('(seller: walmart)');
                    if (supplierLower === 'walmart' && !hasWalmartSeller) return false;
                }

                // Walmart badge filter
                if (filters.walmart.selected_badges.length > 0) {
                    const dealBadges = (data.badges || []).map(b => b.text);
                    const hasBadge = filters.walmart.selected_badges.some(selectedBadge =>
                        dealBadges.includes(selectedBadge)
                    );
                    if (!hasBadge) return false;
                }

                // Brand exclusion (shared)
                const excludedBrands = JSON.parse(localStorage.getItem('excluded_brands') || '[]');
                const brand = (data.brand || '').toLowerCase();
                if (excludedBrands.some(b => brand === b.toLowerCase())) return false;

                // Category filter (shared) - ADD THIS
                if (filters.shared.selected_categories.length > 0) {
                    const dealCategory = data.category || '';
                    if (!filters.shared.selected_categories.includes(dealCategory)) {
                        return false;
                    }
                }

                if (filters.walmart.min_offers >= 0 && (data.offers || 0) < filters.walmart.min_offers) return false;
                if (filters.walmart.max_offers >= 0 && (data.offers || 0) > filters.walmart.max_offers) return false;

                if (filters.walmart.min_reviews > 0 && (data.number_of_reviews || 0) < filters.walmart.min_reviews) return false;
                if (filters.walmart.min_rating > 0 && (data.average_rating || 0) < filters.walmart.min_rating) return false;

            } else if (deal.marketplace === 'ebay') {
                sellPrice = data.lowest_price || 0;
                const totalSale = sellPrice;
                let finalValueFee = totalSale <= 7500 ? totalSale * 0.136 : (7500 * 0.136) + ((totalSale - 7500) * 0.0235);
                const storeDiscount = { none: 0, basic: 0.066, premium: 0.088, anchor: 0.102, enterprise: 0.110 }[feeSettings.storeSubscription || 'none'] || 0;
                const topRatedDiscount = feeSettings.topRated ? 0.10 : 0;
                finalValueFee = finalValueFee * (1 - storeDiscount - topRatedDiscount);
                const perOrderFee = totalSale <= 10 ? 0.30 : 0.40;
                const paymentProcessingFee = (totalSale * 0.0235) + 0.30;
                const salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
                const packagingCost = feeSettings.packagingCost || 0;
                const otherCosts = feeSettings.otherCosts || 0;
                const totalFees = finalValueFee + perOrderFee + paymentProcessingFee + salesTax + packagingCost + otherCosts;
                profit = sellPrice - sourcePrice - totalFees;
                roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

                // eBay-specific filters
                if (filters.ebay.min_offers >= 0 && (data.total_offers || 0) < filters.ebay.min_offers) return false;
                if (filters.ebay.max_offers >= 0 && (data.total_offers || 0) > filters.ebay.max_offers) return false;
                if (filters.ebay.min_est_sales > 0 && (data.estimated_monthly_sales || 0) < filters.ebay.min_est_sales) return false;

                // Walmart 3P filter for eBay marketplace
                if (filters.ebay.exclude_walmart_3p) {
                    const supplierLower = (deal.supplier_name || '').toLowerCase();
                    const titleLower = (deal.title || '').toLowerCase();
                    const hasWalmartSeller = titleLower.includes('(seller: walmart.com)') || titleLower.includes('(seller: walmart)');
                    if (supplierLower === 'walmart' && !hasWalmartSeller) return false;
                }
            }

            if (!deal.supplier_name) {
                profit = 0;
            }

            // Apply sell price filters
            if (filters.min_sell_price > 0 && sellPrice < filters.min_sell_price) return false;
            if (filters.max_sell_price > 0 && sellPrice > filters.max_sell_price) return false;

            // Apply profit/ROI filters
            if (filters.profitable_only && profit <= 0) return false;
            if (filters.min_roi >= 0 && roi < filters.min_roi) return false;
            if (filters.max_roi >= 0 && roi > filters.max_roi) return false;
            if (filters.min_profit >= 0 && profit < filters.min_profit) return false;
            if (filters.max_profit >= 0 && profit > filters.max_profit) return false;

            return true;
        });

        // Group deals by product_id after filtering
        const groupedDeals = groupDealsByProduct(result);

        // Sort grouped deals: found deals first, then searching/pending/processing, then failed/no_match, then not_scanned
        const sortedDeals = groupedDeals.sort((a, b) => {
            try {
                if (sortBy === 'default') {
                    const getPriority = (status) => {
                        if (status === 'found' || status === 'completed') return 1;
                        if (status === 'pending' || status === 'searching' || status === 'processing') return 2;
                        if (status === 'not_scanned') return 3;
                        if (status === 'failed' || status === 'no_match') return 4;
                        return 5;
                    };
                    return getPriority(a.primaryDeal?.reverse_scan_status) - getPriority(b.primaryDeal?.reverse_scan_status);
                }

                const calcSimpleMetrics = (deal) => {
                    try {
                        const couponPercent = filters.coupon_amount || 0;
                        const sourcePrice = (deal.source_price || 0) * (1 - couponPercent / 100) * getEffectivePack(deal, filters.autoPackDetection);
                        const d = deal.marketplace_data || {};
                        const feeSettings = getFeeSettings(deal.marketplace) || {};
                        const itemWeight = d.weight || deal.weight || 1;
                        let sellPrice = 0, profit = 0;

                        if (deal.marketplace === 'amazon') {
                            sellPrice = d.az_price || 0;
                            const fbaFee = filters.amazon?.fulfillment === 'fba' ? (d.fba_fee || 0) : 0;
                            const refFee = sellPrice * (d.ref_fee_perc || 0.15);
                            const placementFees = filters.amazon?.fulfillment === 'fba' ? (feeSettings.placementFees || 0) : 0;
                            const inboundShipping = filters.amazon?.fulfillment === 'fba' ? (feeSettings.inboundShipping || 0) * itemWeight : 0;
                            const salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
                            const otherCosts = feeSettings.otherCosts || 0;
                            const fulfillmentCost = filters.amazon?.fulfillment !== 'fba' ? (feeSettings.fulfillmentCost || 0) : 0;
                            profit = sellPrice - sourcePrice - (fbaFee + refFee + placementFees + inboundShipping + salesTax + otherCosts + fulfillmentCost);

                        } else if (deal.marketplace === 'walmart') {
                            sellPrice = d.price_value || 0;
                            let wfsFee = 0;
                            if (filters.walmart?.fulfillment === 'wfs') {
                                try {
                                    wfsFee = (d.dimensions && itemWeight)
                                        ? calculateWFSFee(d.dimensions, itemWeight, {
                                            isApparel: d.category?.toLowerCase().includes("clothing") || d.category?.toLowerCase().includes("apparel"),
                                            retailPrice: sellPrice
                                        })
                                        : 3.45;
                                } catch {
                                    wfsFee = 3.45; // fallback to default fee
                                }
                            }
                            const refFee = calculateReferralFee(sellPrice, d.category, d.sub_category) || 0;
                            const inboundShipping = filters.walmart?.fulfillment === 'wfs' ? (feeSettings.inboundShipping || 0) * itemWeight : 0;
                            const salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
                            const otherCosts = feeSettings.otherCosts || 0;
                            const fulfillmentCost = filters.walmart?.fulfillment !== 'wfs' ? (feeSettings.fulfillmentCost || 0) : 0;
                            profit = sellPrice - sourcePrice - (wfsFee + refFee + inboundShipping + salesTax + otherCosts + fulfillmentCost);

                        } else if (deal.marketplace === 'ebay') {
                            sellPrice = d.lowest_price || 0;
                            let finalValueFee = sellPrice <= 7500
                                ? sellPrice * 0.136
                                : (7500 * 0.136) + ((sellPrice - 7500) * 0.0235);
                            const storeDiscount = { none: 0, basic: 0.066, premium: 0.088, anchor: 0.102, enterprise: 0.110 }[feeSettings.storeSubscription || 'none'] || 0;
                            const topRatedDiscount = feeSettings.topRated ? 0.10 : 0;
                            finalValueFee *= (1 - storeDiscount - topRatedDiscount);
                            const perOrderFee = sellPrice <= 10 ? 0.30 : 0.40;
                            const paymentProcessingFee = (sellPrice * 0.0235) + 0.30;
                            const salesTax = sourcePrice * ((feeSettings.salesTax || 0) / 100);
                            const packagingCost = feeSettings.packagingCost || 0;
                            const otherCosts = feeSettings.otherCosts || 0;
                            profit = sellPrice - sourcePrice - (finalValueFee + perOrderFee + paymentProcessingFee + salesTax + packagingCost + otherCosts);
                        }

                        if (!deal.supplier_name) profit = 0;
                        const roi = sourcePrice > 0 ? (profit / sourcePrice) * 100 : 0;
                        return { profit, roi, cost: sourcePrice, sellPrice };

                    } catch (err) {
                        console.warn('calcSimpleMetrics failed for deal:', deal?.id, err);
                        return { profit: 0, roi: 0, cost: 0, sellPrice: 0 };
                    }
                };

                const mA = calcSimpleMetrics(a.primaryDeal);
                const mB = calcSimpleMetrics(b.primaryDeal);

                switch (sortBy) {
                    case 'roi_desc': return mB.roi - mA.roi;
                    case 'roi_asc': return mA.roi - mB.roi;
                    case 'profit_desc': return mB.profit - mA.profit;
                    case 'profit_asc': return mA.profit - mB.profit;
                    case 'cost_asc': return mA.cost - mB.cost;
                    case 'cost_desc': return mB.cost - mA.cost;
                    case 'sell_asc': return mA.sellPrice - mB.sellPrice;
                    case 'sell_desc': return mB.sellPrice - mA.sellPrice;
                    case 'date_desc': {
                        const dB = new Date(b.primaryDeal?.updated_at || b.primaryDeal?.created_at);
                        const dA = new Date(a.primaryDeal?.updated_at || a.primaryDeal?.created_at);
                        return (isNaN(dB) ? 0 : dB) - (isNaN(dA) ? 0 : dA);
                    }
                    case 'date_asc': {
                        const dA = new Date(a.primaryDeal?.updated_at || a.primaryDeal?.created_at);
                        const dB = new Date(b.primaryDeal?.updated_at || b.primaryDeal?.created_at);
                        return (isNaN(dA) ? 0 : dA) - (isNaN(dB) ? 0 : dB);
                    }
                    case 'bsr_asc': {
                        const bA = a.primaryDeal?.marketplace_data?.sales_rank || 999999999;
                        const bB = b.primaryDeal?.marketplace_data?.sales_rank || 999999999;
                        return bA - bB;
                    }
                    case 'bsr_desc': {
                        const bA = a.primaryDeal?.marketplace_data?.sales_rank || 0;
                        const bB = b.primaryDeal?.marketplace_data?.sales_rank || 0;
                        return bB - bA;
                    }
                    case 'bsr_drops_desc': {
                        const dA = a.primaryDeal?.marketplace_data?.sales_rank_drops_30_days || 0;
                        const dB = b.primaryDeal?.marketplace_data?.sales_rank_drops_30_days || 0;
                        return dB - dA;
                    }
                    case 'sales_desc': {
                        const sA = a.primaryDeal?.marketplace_data?.keepa_monthly_sold > 0
                            ? a.primaryDeal.marketplace_data.keepa_monthly_sold
                            : (a.primaryDeal?.marketplace_data?.estimate_sales_30_days || 0);
                        const sB = b.primaryDeal?.marketplace_data?.keepa_monthly_sold > 0
                            ? b.primaryDeal.marketplace_data.keepa_monthly_sold
                            : (b.primaryDeal?.marketplace_data?.estimate_sales_30_days || 0);
                        return sB - sA;
                    }
                    case 'amazon_offers_asc': {
                        const oA = a.primaryDeal?.marketplace_data?.total_offers_count || 999999;
                        const oB = b.primaryDeal?.marketplace_data?.total_offers_count || 999999;
                        return oA - oB;
                    }
                    case 'amazon_offers_desc': {
                        const oA = a.primaryDeal?.marketplace_data?.total_offers_count || 0;
                        const oB = b.primaryDeal?.marketplace_data?.total_offers_count || 0;
                        return oB - oA;
                    }
                    case 'walmart_reviews_desc': {
                        const rA = a.primaryDeal?.marketplace_data?.number_of_reviews || 0;
                        const rB = b.primaryDeal?.marketplace_data?.number_of_reviews || 0;
                        return rB - rA;
                    }
                    case 'walmart_offers_asc': {
                        const oA = a.primaryDeal?.marketplace_data?.offers || 999999;
                        const oB = b.primaryDeal?.marketplace_data?.offers || 999999;
                        return oA - oB;
                    }
                    case 'walmart_offers_desc': {
                        const oA = a.primaryDeal?.marketplace_data?.offers || 0;
                        const oB = b.primaryDeal?.marketplace_data?.offers || 0;
                        return oB - oA;
                    }
                    default: return 0;
                }

            } catch (err) {
                console.warn('Sort comparator failed:', err);
                return 0; // keep original order on unexpected failure
            }
        });

        startTransition(() => setFilteredDeals(skeletonGroups.length ? [...sortedDeals, ...skeletonGroups] : sortedDeals));
    }, [deals, filters, settingsUpdateTrigger, sortBy]);

    const onRefreshGated = async (dealId) => {
        setRefreshing(true);
        try {
            const isUniversalLiveScan = pageName === 'universal-live-scan';

            if (isUniversalLiveScan) {
                const dealToUpdate = deals.find(d => d.id === dealId);
                const asin = dealToUpdate?.marketplace_data?.asin;

                if (!asin) {
                    alert('No ASIN found for this deal.');
                    return;
                }

                const response = await authService.authenticatedFetch(
                    API_BASE + '/api/a2a/check-gated-bulk',
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ asins: [asin], skip_cache: false })
                    }
                );
                const data = await response.json();

                if (data.results && data.results[asin]) {
                    if (dealToUpdate) {
                        dealToUpdate.gated_status = data.results[asin];
                        setSettingsUpdateTrigger(prev => prev + 1);
                    }
                }
            } else {
                const response = await authService.authenticatedFetch(
                    API_BASE + `/api/${pageName}/deals/${dealId}/refresh-gated`,
                    { method: 'POST' }
                );
                const data = await response.json();

                if (data.success && data.gated_status) {
                    const dealToUpdate = deals.find(d => d.id === dealId);
                    if (dealToUpdate) {
                        dealToUpdate.gated_status = data.gated_status;
                        setSettingsUpdateTrigger(prev => prev + 1);
                    }
                }
            }
        } catch (error) {
            alert(error.message || 'Failed to refresh gated status');
        } finally {
            setRefreshing(false);
        }
    };

    const onHideDeal = (dealId, skipConfirmation = false) => {
        if (!skipConfirmation) {
            const confirmed = window.confirm(
                'Are you sure you want to remove this deal?'
            );
            if (!confirmed) return;
        }

        setHiddenDeals(prev => {
            const updated = [...prev, dealId];
            localStorage.setItem('hidden_deals', JSON.stringify(updated));
            return updated;
        });

        // Trigger re-filter
        setSettingsUpdateTrigger(prev => prev + 1);
    };

    // Silently re-check gated status for a single deal — used for background
    // auto-refresh of 'error' rows as they appear on screen. Deliberately does
    // NOT touch the global `refreshing` flag or show alerts, since this runs
    // automatically in the background rather than from a user click.
    const silentRefreshGated = async (dealId) => {
        try {
            const isUniversalLiveScan = pageName === 'universal-live-scan';

            if (isUniversalLiveScan) {
                const dealToUpdate = deals.find(d => d.id === dealId);
                const asin = dealToUpdate?.marketplace_data?.asin;
                if (!asin) return;

                const response = await authService.authenticatedFetch(
                    API_BASE + '/api/a2a/check-gated-bulk',
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ asins: [asin], skip_cache: false })
                    }
                );
                const data = await response.json();

                if (data.results && data.results[asin] && dealToUpdate) {
                    dealToUpdate.gated_status = data.results[asin];
                    setSettingsUpdateTrigger(prev => prev + 1);
                }
            } else {
                const response = await authService.authenticatedFetch(
                    API_BASE + `/api/${pageName}/deals/${dealId}/refresh-gated`,
                    { method: 'POST' }
                );
                const data = await response.json();

                if (data.success && data.gated_status) {
                    const dealToUpdate = deals.find(d => d.id === dealId);
                    if (dealToUpdate) {
                        dealToUpdate.gated_status = data.gated_status;
                        setSettingsUpdateTrigger(prev => prev + 1);
                    }
                }
            }
        } catch (error) {
            console.error(`Silent gated refresh failed for ${dealId}:`, error);
        }
    };

    // Sequential background queue: whenever the filtered result set changes
    // (new filter/search applied, or a deal's status just got updated), rebuild
    // the full queue of 'error' deals across ALL pages, in display order, and
    // process them one at a time — each refresh awaited before the next starts,
    // no delay in between. If filteredDeals changes again mid-run (including as
    // a side effect of a refresh completing further down in this same run), the
    // in-flight loop is abandoned and a fresh queue is built from current state.
    // Deals already fixed are simply no longer 'error' so they drop out of the
    // new queue on their own — no separate "already done" tracking needed.
    const queueRunIdRef = useRef(0);

    useEffect(() => {
        const runId = ++queueRunIdRef.current;

        const errorQueue = filteredDeals
            .map(g => g.primaryDeal)
            .filter(d => d.marketplace === 'amazon' && d.gated_status === 'error');

        if (errorQueue.length === 0) return;

        (async () => {
            for (const deal of errorQueue) {
                if (queueRunIdRef.current !== runId) return; // superseded by a newer queue
                await silentRefreshGated(deal.id);
            }
        })();
    }, [filteredDeals]);

    return (
        <div className="bg-white rounded-xl shadow-2xl">
            {/* Header */}
            <div className="p-6 border-b" id="deals-viewer">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">{title} <button
                    onClick={(e) => { e.stopPropagation(); window.open('https://www.youtube.com/watch?v=Ll5H_Jh1TFM', '_blank'); }}
                    className="text-[11px] text-blue-500 hover:text-blue-700 underline underline-offset-2"
                    title="See which exact ASINs you're ungated for"
                >
                    ▶ FULL MSP Filters tutorial.
                </button></h2>

                {/* Filters */}
                {showFilters && (
                    <AdvancedFilters
                        filters={filters}
                        setFilters={setFilters}
                        marketplace={filters.marketplace}
                        exportToCSV={() => exportToCSV(filteredDeals, filters)}
                        initialFilters={initialFilters}
                        walmartBadges={getAllWalmartBadges()}
                        allSuppliers={getAllSuppliers()}
                        allCategories={getAllCategories()}
                        allBrands={getAllBrands()}
                        onSettingsChange={() => setSettingsUpdateTrigger(prev => prev + 1)}
                        isCompactView={isCompactView}
                        setIsCompactView={setIsCompactView}
                        afRender={afRender}
                        setAFRender={setAFRender}
                        hasUnscannedDeals={(() => { const real = filteredDeals.filter(g => !g.primaryDeal.is_skeleton); return real.length > 0 && real.every(g => g.primaryDeal.reverse_scan_status === 'not_scanned'); })()}
                        autoPackDetection={filters.autoPackDetection}
                        setAutoPackDetection={(val) => setFilters(prev => ({ ...prev, autoPackDetection: val }))}
                    />
                )}
            </div>

            {/* Deals List */}
            <div className="p-6">
                {loading ? (
                    <div className="text-center py-12">
                        <RefreshCw className="animate-spin mx-auto text-teal-600 mb-4" size={48} />
                        <p className="text-gray-600">Loading deals...</p>
                    </div>
                ) : filteredDeals.length === 0 ? (
                    <div className="text-center py-12">
                        <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
                        <p className="text-gray-600">No deals found with current filters</p>
                    </div>
                ) : (
                    <>
                        {/* Pagination Info */}
                        <div className="mb-4 text-sm text-gray-600 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <select
                                    value={sortBy}
                                    onChange={e => setSortBy(e.target.value)}
                                    className="px-2 py-1 text-xs border border-gray-300 rounded-lg"
                                >
                                    <option value="default">Sort: Default (Status)</option>
                                    <option value="roi_desc">ROI: High → Low</option>
                                    <option value="roi_asc">ROI: Low → High</option>
                                    <option value="profit_desc">Profit: High → Low</option>
                                    <option value="profit_asc">Profit: Low → High</option>
                                    <option value="cost_asc">Cost: Low → High</option>
                                    <option value="cost_desc">Cost: High → Low</option>
                                    <option value="sell_asc">Sell Price: Low → High</option>
                                    <option value="sell_desc">Sell Price: High → Low</option>
                                    <option value="date_desc">Newest First</option>
                                    <option value="date_asc">Oldest First</option>
                                    <option value="bsr_asc">BSR: Low → High</option>
                                    <option value="bsr_desc">BSR: High → Low</option>
                                    <option value="bsr_drops_desc">BSR Drops 30d: High → Low</option>
                                    <option value="sales_desc">Est. Sales: High → Low</option>
                                    <option value="amazon_offers_asc">Amazon Offers: Low → High</option>
                                    <option value="amazon_offers_desc">Amazon Offers: High → Low</option>
                                    <option value="walmart_reviews_desc">Walmart Reviews: High → Low</option>
                                    <option value="walmart_offers_asc">Walmart Offers: Low → High</option>
                                    <option value="walmart_offers_desc">Walmart Offers: High → Low</option>
                                </select>
                                <span>
                                    Showing {indexOfFirstDeal + 1} - {Math.min(indexOfLastDeal, filteredDeals.length)} of {filteredDeals.length} products
                                </span>
                                {/* Warning Modal */}
                                {showSupplierPicker && (
                                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 mt-14">
                                        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[80vh] flex flex-col">
                                            <h3 className="text-lg font-bold text-gray-900 mb-1">Select Suppliers to Search</h3>
                                            <p className="text-sm text-gray-500 mb-1">
                                                {supplierPickerData.is_unlimited
                                                    ? 'Unlimited plan - all suppliers available'
                                                    : `Pro plan - select up to ${supplierPickerData.max_suppliers} suppliers`}
                                            </p>
                                            <p className="text-xs text-gray-400 mb-4">
                                                {selectedSuppliers.length} / {supplierPickerData.is_unlimited ? supplierPickerData.suppliers.length : supplierPickerData.max_suppliers} selected
                                            </p>
                                            <div className="flex gap-2 mb-3">
                                                <button
                                                    onClick={() => setSelectedSuppliers([...supplierPickerData.suppliers])}
                                                    disabled={!supplierPickerData.is_unlimited}
                                                    className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-40"
                                                >
                                                    Select All
                                                </button>
                                                <button
                                                    onClick={() => setSelectedSuppliers([])}
                                                    className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg"
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                            <div className="overflow-y-auto flex-1 grid grid-cols-2 gap-2 pr-1">
                                                {supplierPickerData.suppliers.map(supplier => {
                                                    const isSelected = selectedSuppliers.includes(supplier);
                                                    const isDisabled = !isSelected && !supplierPickerData.is_unlimited && selectedSuppliers.length >= supplierPickerData.max_suppliers;
                                                    return (
                                                        <label
                                                            key={supplier}
                                                            className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition ${isSelected ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'} ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                disabled={isDisabled}
                                                                onChange={() => {
                                                                    if (isSelected) {
                                                                        setSelectedSuppliers(prev => prev.filter(s => s !== supplier));
                                                                    } else if (!isDisabled) {
                                                                        setSelectedSuppliers(prev => [...prev, supplier]);
                                                                    }
                                                                }}
                                                                className="w-4 h-4 text-teal-600"
                                                            />
                                                            <span className="text-sm text-gray-700 capitalize">{supplier}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                            <div className="flex gap-3 mt-4">
                                                <button
                                                    onClick={() => setShowSupplierPicker(false)}
                                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setShowSupplierPicker(false);
                                                        handleRevealAll(selectedSuppliers);
                                                    }}
                                                    disabled={selectedSuppliers.length === 0}
                                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-teal-500 to-blue-500 text-white text-sm font-semibold rounded-lg hover:opacity-90 disabled:opacity-50"
                                                >
                                                    Continue ({selectedSuppliers.length} suppliers)
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {showSearchingPopup && (
                                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
                                            <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-5">
                                                <RefreshCw className="text-teal-500 animate-spin" size={32} />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-2">Searching for Suppliers!</h3>
                                            <p className="text-gray-600 text-sm mb-4">
                                                We're scanning all selected suppliers in the background. This may take some time.
                                            </p>
                                            <p className="text-gray-500 text-sm mb-6">
                                                When results are ready, an <span className="font-semibold text-teal-600">Update</span> button will appear at the bottom of the screen - click it to load the latest deals. You can safely close this page and come back later.
                                            </p>
                                            <button
                                                onClick={() => setShowSearchingPopup(false)}
                                                className="w-full px-4 py-3 bg-gradient-to-r from-teal-500 to-blue-500 text-white font-semibold rounded-xl hover:opacity-90 transition"
                                            >
                                                Got it!
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {showRevealWarning && (
                                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                                            <div className="flex items-start gap-3 mb-4">
                                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                                    <AlertCircle className="text-amber-500" size={20} />
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-bold text-gray-900 mb-1">Reveal All Suppliers Across All Products</h3>
                                                    <p className="text-sm text-gray-600">
                                                        ⚠️ <span className="font-semibold text-red-600">Important:</span> This will scan suppliers for <span className="font-semibold">all unscanned products</span> across all pages - not just this one.
                                                    </p>
                                                    <p className="text-sm text-gray-600 mt-2">
                                                        For best results, make sure you've filtered by your criteria first using the{' '}
                                                        <span className="font-semibold text-teal-600">General Filters</span> and{' '}
                                                        <span className="font-semibold text-teal-600">Marketplace Related Filters</span> before proceeding.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
                                                <p className="text-xs text-amber-800">
                                                    This will reveal <span className="font-bold">{unrevealedTotal.length}</span> products across all pages.
                                                    Revealing takes time - filtering first avoids wasting scans on deals you don't want.
                                                </p>
                                            </div>
                                            <div className="rounded-xl overflow-hidden mb-8" style={{ aspectRatio: '16/9' }}>
                                                <p className="text-center text-xs font-bold text-red-600 mb-2">🎬 YOU MUST WATCH - IT'S A GAME CHANGER!</p>
                                                <iframe
                                                    width="100%"
                                                    height="100%"
                                                    src="https://www.youtube.com/embed/sFWdAhKzLuw"
                                                    title="Universal Live Scan Tutorial"
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                                    allowFullScreen
                                                    style={{ display: 'block', width: '100%', height: '100%', minHeight: 180 }}
                                                />
                                            </div>
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={() => setShowRevealWarning(false)}
                                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition"
                                                >
                                                    Go Back & Filter
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setShowRevealWarning(false);
                                                        setShowSupplierPicker(true);
                                                    }}
                                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-teal-500 to-blue-500 text-white text-sm font-semibold rounded-lg hover:opacity-90 transition"
                                                >
                                                    Continue & Reveal All
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Active reverse scan indicator */}
                                {(pageName === 'storefront' || pageName === 'reverse-scan') && (() => {
                                    const activeCount = filteredDeals
                                        .map(g => g.primaryDeal)
                                        .filter(d => d.reverse_scan_status === 'pending' || d.reverse_scan_status === 'searching' || d.reverse_scan_status === 'processing').length;
                                    return activeCount > 0 ? (
                                        <span className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold rounded-lg">
                                            <RefreshCw size={12} className="animate-spin" />
                                            Searching suppliers for {activeCount} product{activeCount !== 1 ? 's' : ''}...
                                        </span>
                                    ) : null;
                                })()}

                                {/* Reveal All / Stop button */}
                                {(pageName === 'storefront' || pageName === 'reverse-scan') && unrevealedTotal.length > 0 && (revealingAll ? (
                                    <button
                                        onClick={stopRevealAll}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-lg hover:bg-red-600 transition shadow-sm"
                                    >
                                        <Square size={12} /> Stop ({revealProgress.current}/{revealProgress.total})
                                    </button>
                                ) : (
                                    <button
                                        onClick={async () => {
                                            if (authService.getAmazonMarketplace() !== '.com') {
                                                alert('Reverse Scan supplier reveal is coming soon for your marketplace!');
                                                return;
                                            }
                                            const res = await authService.authenticatedFetch(API_BASE + '/api/reverse-scan/suppliers');
                                            const data = await res.json();
                                            setSupplierPickerData(data);
                                            setShowRevealWarning(true);
                                        }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold rounded-lg transition shadow-sm ${authService.getAmazonMarketplace() !== '.com' ? 'bg-gray-400 cursor-not-allowed opacity-60' : 'bg-gradient-to-r from-teal-500 to-blue-500 hover:opacity-90'}`}
                                        title={authService.getAmazonMarketplace() !== '.com' ? 'Coming soon for your marketplace' : 'Reveal all suppliers across all pages'}
                                    >
                                        <Eye size={12} /> Reveal All Suppliers ({unrevealedTotal.length}){authService.getAmazonMarketplace() !== '.com' && ' 🔜'}
                                    </button>
                                ))}
                            </div>
                            {totalPages > 1 && (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => { setCurrentPage(prev => Math.max(1, prev - 1)); document.getElementById('deals-viewer')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                    >
                                        <ChevronLeft size={16} />
                                        Previous
                                    </button>
                                    <PageJumpInput
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onJump={(page) => { setCurrentPage(page); document.getElementById('deals-viewer')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                                    />
                                    <button
                                        onClick={() => { setCurrentPage(prev => Math.min(totalPages, prev + 1)); document.getElementById('deals-viewer')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                                        disabled={currentPage === totalPages}
                                        className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                    >
                                        Next
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Deals Grid */}
                        <div className={`space-y-4 overflow-visible transition-opacity duration-150 ${isPending ? 'opacity-60' : 'opacity-100'}`}>
                            {currentDeals.map(groupedDeal => (
                                groupedDeal.primaryDeal.is_skeleton ? (
                                    <SkeletonDealRow key={groupedDeal.primaryDeal.id} deal={groupedDeal.primaryDeal} />
                                ) : isCompactView ? (
                                    <MinimizedDealCard
                                        key={`${groupedDeal.primaryDeal.id}-${settingsUpdateTrigger}`}
                                        deal={groupedDeal.primaryDeal}
                                        alternativeSources={groupedDeal.alternativeSources}
                                        couponAmount={filters.coupon_amount}
                                        fulfillmentType={groupedDeal.primaryDeal.marketplace === 'amazon' ? filters.amazon.fulfillment : groupedDeal.primaryDeal.marketplace === 'walmart' ? filters.walmart.fulfillment : null}
                                        onFulfillmentChange={(newType) => {
                                            const marketplace = groupedDeal.primaryDeal.marketplace;
                                            if (marketplace === 'amazon') {
                                                setFilters(prev => ({ ...prev, amazon: { ...prev.amazon, fulfillment: newType } }));
                                            } else if (marketplace === 'walmart') {
                                                setFilters(prev => ({ ...prev, walmart: { ...prev.walmart, fulfillment: newType } }));
                                            }
                                        }}
                                        onRefreshGated={onRefreshGated ? () => onRefreshGated(groupedDeal.primaryDeal.id) : null}
                                        refreshing={refreshing}
                                        onSettingsChange={() => setSettingsUpdateTrigger(prev => prev + 1)}
                                        onHideDeal={onHideDeal}
                                        amazonLinked={amazonLinked}
                                        autoPackDetection={filters.autoPackDetection ?? true}
                                    />
                                ) : (
                                    <DealCard
                                        key={`${groupedDeal.primaryDeal.id}-${settingsUpdateTrigger}`}
                                        deal={groupedDeal.primaryDeal}
                                        alternativeSources={groupedDeal.alternativeSources}
                                        couponAmount={filters.coupon_amount}
                                        fulfillmentType={groupedDeal.primaryDeal.marketplace === 'amazon' ? filters.amazon.fulfillment : groupedDeal.primaryDeal.marketplace === 'walmart' ? filters.walmart.fulfillment : null}
                                        onFulfillmentChange={(newType) => {
                                            const marketplace = groupedDeal.primaryDeal.marketplace;
                                            if (marketplace === 'amazon') {
                                                setFilters(prev => ({ ...prev, amazon: { ...prev.amazon, fulfillment: newType } }));
                                            } else if (marketplace === 'walmart') {
                                                setFilters(prev => ({ ...prev, walmart: { ...prev.walmart, fulfillment: newType } }));
                                            }
                                        }}
                                        onRefreshGated={onRefreshGated ? () => onRefreshGated(groupedDeal.primaryDeal.id) : null}
                                        refreshing={refreshing}
                                        onSettingsChange={() => setSettingsUpdateTrigger(prev => prev + 1)}
                                        onHideDeal={onHideDeal}
                                        amazonLinked={amazonLinked}
                                        autoPackDetection={filters.autoPackDetection ?? true}
                                    />
                                )
                            ))}
                        </div>

                        {/* Bottom Pagination */}
                        {totalPages > 1 && (
                            <div className="mt-6 flex items-center justify-center gap-2">
                                <button
                                    onClick={() => { setCurrentPage(prev => Math.max(1, prev - 1)); document.getElementById('deals-viewer')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                >
                                    <ChevronLeft size={16} />
                                    Previous
                                </button>
                                <PageJumpInput
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onJump={(page) => { setCurrentPage(page); document.getElementById('deals-viewer')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                                />
                                <button
                                    onClick={() => { setCurrentPage(prev => Math.min(totalPages, prev + 1)); document.getElementById('deals-viewer')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                >
                                    Next
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DealsViewer;